/**
 * Audio context utilities for Web/Browser
 *
 * Sets up standardized-audio-context for consistent browser support.
 * Uses standalone Transport - no Tone.js dependency.
 */

import {
  AudioContext as StandardizedAudioContext,
  AudioWorkletNode as StandardizedAudioWorkletNode
} from 'standardized-audio-context';
import type { RuntimeEnv } from '@runtime/RuntimeEnv';
import { Transport } from '@runtime/Transport';
import { AnimationLoop } from '@runtime/AnimationLoop';

// Override global AudioContext and AudioWorkletNode with standardized versions
// This ensures Elementary and other libraries use the same context type
(globalThis as any).AudioContext = StandardizedAudioContext;
(globalThis as any).AudioWorkletNode = StandardizedAudioWorkletNode;

let audioContext: StandardizedAudioContext | null = null;
let transport: Transport | null = null;
let animationLoop: AnimationLoop | null = null;

/**
 * Initialize the audio context for the browser.
 * Must be called after a user gesture (click/tap) due to browser autoplay policies.
 */
export async function initWebAudioContext(): Promise<void> {
  if (!audioContext) {
    audioContext = new StandardizedAudioContext();

    // Resume context (required after user gesture)
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
  }
}

/**
 * Get the standalone transport for runtime DI.
 */
export function getWebTransport(): Transport {
  if (!transport) {
    // Initialize context if not already done
    if (!audioContext) {
      audioContext = new StandardizedAudioContext();
    }

    transport = new Transport({
      audioContext: audioContext as unknown as AudioContext,
      bpm: 120,
      ppq: 48
    });
  }

  return transport;
}

/**
 * Check if audio context is initialized and running
 */
export function isAudioContextReady(): boolean {
  return audioContext !== null && audioContext.state === 'running';
}

/**
 * Resume audio context (needed after user gesture)
 */
export async function resumeAudioContext(): Promise<void> {
  if (audioContext && audioContext.state === 'suspended') {
    await audioContext.resume();
  }
}

/**
 * Close the audio context and stop transport
 */
export async function closeWebAudioContext(): Promise<void> {
  if (animationLoop) {
    animationLoop.dispose();
    animationLoop = null;
  }

  if (transport) {
    transport.dispose();
    transport = null;
  }

  if (audioContext) {
    await audioContext.close();
    audioContext = null;
  }
}

/**
 * Get the raw standardized-audio-context (for direct access to AudioContext API)
 */
export function getAudioContext(): StandardizedAudioContext | null {
  return audioContext;
}

/**
 * Get the full runtime environment for web/browser
 */
export function getWebEnv(): RuntimeEnv {
  const transport = getWebTransport();

  return {
    audioContext: audioContext as unknown as AudioContext,
    transport
  };
}
